import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight "who is online" support built on Realtime presence.
 *
 * Every signed-in session joins the shared `online-users` channel and tracks
 * its own user id. Admin screens read the same channel to list the ids that
 * currently have at least one live session. Nothing is written to the
 * database, so there is no schema or RLS impact.
 */
const CHANNEL = "online-users";

/** Announce the current session as online for as long as it is mounted. */
export function usePresenceTracker(userId: string | null | undefined) {
  useEffect(() => {
    if (!userId) return;
    const channel = supabase.channel(CHANNEL, {
      config: { presence: { key: userId } },
    });
    channel.subscribe((status) => {
      if (status === "SUBSCRIBED") {
        void channel.track({ online_at: new Date().toISOString() });
      }
    });
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId]);
}

/** Set of user ids with at least one live session right now. */
export function useOnlineUserIds(enabled = true): Set<string> {
  const [ids, setIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled) return;
    const channel = supabase.channel(CHANNEL, {
      config: { presence: { key: `observer-${Math.random().toString(36).slice(2)}` } },
    });
    const sync = () => {
      const state = channel.presenceState();
      setIds(new Set(Object.keys(state).filter((k) => !k.startsWith("observer-"))));
    };
    channel
      .on("presence", { event: "sync" }, sync)
      .on("presence", { event: "join" }, sync)
      .on("presence", { event: "leave" }, sync)
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [enabled]);

  return ids;
}
